import classNames from 'classnames'
import { t } from 'i18next'
import { ReactElement, ReactNode, SyntheticEvent } from 'react'
import { IconFa } from '../icon-fa'
import { IconLoading } from '../icon-loading'
import styles from './index.module.css'

export type FeedbackSize = 'md' | 'sm' | 'xs'

export type FeedbackVariant =
  | 'info-light'
  | 'info-dark'
  | 'loading-light'
  | 'loading-dark'
  | 'success-light'
  | 'success-dark'
  | 'warning-light'
  | 'warning-dark'
  | 'error-light'
  | 'error-dark'
  | 'transparent'

interface Props {
  id?: string
  text?: ReactNode
  body?: string
  icon?: ReactNode
  noIcon?: boolean
  onClose?: (event: SyntheticEvent) => void
  variant: FeedbackVariant
  className?: string
  textClassName?: string
  border?: boolean
  fullWidth?: boolean
  size?: FeedbackSize
  hideCloseButton?: boolean
  containerClassName?: string
}

export function FeedbackBox(props: Props): ReactElement {
  const className = classNames(
    styles.feedback,
    styles[props.variant],
    props.border && styles.hasBorder,
    props.size === 'sm' && styles.small,
    props.size === 'xs' && styles.xs,
    props.noIcon && styles.noIcon,
    props.className
  )

  const closeIcon = props.onClose && (
    <button type="button" onClick={props.onClose} className={styles.close}>
      <IconFa icon={['far', 'times']} title={t('glow.close') ?? ''} />
    </button>
  )

  return (
    <div
      id={props.id}
      className={classNames(props.fullWidth === false ? 'inline-block' : '', props.containerClassName)}
    >
      <div className={className} data-testid={`feedback-box-${props.variant}`}>
        <span className={styles.content}>
          {!props.noIcon && (props.icon ?? <FeedbackBoxIcon type={props.variant} />)}
          {props.text && (
            <span className={classNames(Boolean(props.body) && 'text-lg', props.textClassName)}>{props.text}</span>
          )}
          {props.body && <div className={styles.body}>{props.body}</div>}
        </span>
        {!props.hideCloseButton && closeIcon}
      </div>
    </div>
  )
}

export function FeedbackBoxIcon(props: { type: FeedbackVariant }) {
  switch (props.type) {
    case 'loading-light':
    case 'loading-dark':
      return <IconLoading />
    case 'error-light':
    case 'error-dark':
      return <IconFa icon={['fas', 'exclamation-triangle']} />
    case 'success-light':
    case 'success-dark':
      return <IconFa icon={['fas', 'check-circle']} />
    default:
      return <IconFa icon={['fas', 'exclamation-circle']} />
  }
}
